<script setup>
/**
 * ARRANGE FORM: the owner arranges her task's WhatsApp form into pages (services spec P-4.10
 * design 9). Under the task it edits (`…/waslo/tasks/:taskId/form`, `waslo_task_form`), reached
 * from the Forms tab's task row and from the composer's Check screen.
 *
 * ── THE FORM STAYS FED BY THE TASK ─────────────────────────────────────────────────────
 * Every box is one of the task's own questions, referenced by step id, so every answer lands
 * where the chat would have put it. She chooses which questions go on the form and which the
 * assistant asks in the chat, orders and pages them, lets the customer skip one, and words the
 * form. She cannot add a box nothing reads.
 *
 * ── THE PREVIEW, THE LISTS AND THE ERRORS ARE THE SERVER'S ─────────────────────────────
 * Every change is sent, debounced, to `POST …/tasks/form-preview`, which compiles the unsaved
 * task exactly as a publish would: a pure compile, never an AI call, never charged, nothing
 * saved. The phone, the "Asked in the chat" list and every error come from that answer; this
 * page only moves references (`helpers/form-layout.js`). The page's own `localErrors` say the
 * obvious ones before the answer arrives, and the save holds while any error stands.
 *
 * ── SAVING IS THE TASK'S OWN SAVE ──────────────────────────────────────────────────────
 * `PATCH …/tasks/:id` with the whole definition: the form block and the last page's button
 * words (`ctaEn`/`ctaAr`) inside it, so it is versioned and undoable like any task edit. A page
 * change is form TEXT, not SHAPE (§9.1 I-11): a published form keeps being sent, and the Forms
 * tab says "Live. Your latest wording is not on the form yet." with Update.
 *
 * A task that never had a form block opens on today's questions split into pages, as a change
 * still to save (spec P-4.10 open question 1): its first save moves it to pages and changes only
 * the form's words.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import BilingualBox from '../components/task/BilingualBox.vue';
import FormPageCard from '../components/form/FormPageCard.vue';
import FormPhonePreview from '../components/form/FormPhonePreview.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import WasloSaveBar from '../components/WasloSaveBar.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useCustomerLanguages } from '../composables/useCustomerLanguages.js';
import { toIntlLocale } from '../helpers/intl.js';
import { sameGraph } from '../helpers/task-spine-edit.js';
import {
  MAX_FORM_BUTTON,
  MAX_FORM_INTRO,
  MAX_FORM_PAGES,
  MAX_FORM_QUESTIONS,
  MAX_FORM_TITLE,
  addPage,
  autoForm,
  localErrors,
  mergeErrors,
  moveField,
  moveToPage,
  questionCount,
  questionWords,
  removePage,
  setFormWords,
  setOptional,
  setPageWords,
  toChat,
  toForm,
} from '../helpers/form-layout.js';
import { wasloApi } from '../api/client.js';

/** How long a pause in her edits before the server is asked for a new preview. */
const PREVIEW_DELAY_MS = 400;

const route = useRoute();
const accountId = useWasloAccountId();
const taskId = computed(() => String(route.params.taskId ?? ''));
const languages = useCustomerLanguages(accountId);
const { t, locale } = useWasloI18n();
const key = name => `WASLO.FORMS.ARRANGE.${name}`;
const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);
const number = n => format.value.format(n);
const dashboardLang = computed(() =>
  String(locale.value ?? '').startsWith('ar') ? 'ar' : 'en'
);

const task = useWasloApi(signal =>
  wasloApi.getTask(accountId.value, taskId.value, signal)
);

/** The task as last read or saved. */
const stored = ref(null);
/** Her arrangement since that read, or `null`: unchanged. */
const working = ref(null);
/** The task's submit words, which the last page's button edits. */
const cta = ref({ en: '', ar: '' });
const answer = ref(null);
const previewing = ref(false);
const previewFailed = ref(false);
const previewLang = ref('en');
const isSaving = ref(false);
const saveFailed = ref(false);
const savedAt = ref(null);
/** A save's refusal, kept on screen until her next edit. */
const serverProblems = ref([]);
/** Set while a task with no form block waits for its first preview's `seed`. */
let seedNext = false;
let timer = null;
let asked = 0;

const adopt = value => {
  stored.value = value;
  const definition = value?.definition ?? {};
  cta.value = { en: definition.ctaEn ?? '', ar: definition.ctaAr ?? '' };
  working.value = null;
  answer.value = null;
  serverProblems.value = [];
  seedNext = !definition.form;
};

watch(
  () => task.data.value,
  value => {
    if (value) adopt(value);
  },
  { immediate: true }
);

// The preview opens in the account's language; on a Both account, in the dashboard's.
watch(
  languages,
  value => {
    previewLang.value =
      value === 'ar' || value === 'en' ? value : dashboardLang.value;
  },
  { immediate: true }
);

const storedForm = computed(() => stored.value?.definition?.form ?? null);

/**
 * What the cards show: always arranged pages. An automatic form is shown as the server splits
 * it (`seed`), ready for her first move, which makes it arranged.
 */
const layoutForm = computed(() => {
  const form = working.value ?? storedForm.value;
  if (!form || form.mode === 'auto') return answer.value?.seed ?? null;
  return form;
});
const pages = computed(() => layoutForm.value?.pages ?? []);

/** The definition a save would write: the stored one, with her form and her button words. */
const draftDefinition = computed(() => {
  const definition = stored.value?.definition;
  if (!definition) return null;
  const out = { ...definition };
  delete out.ctaEn;
  delete out.ctaAr;
  delete out.form;
  const en = cta.value.en.trim();
  const ar = cta.value.ar.trim();
  const form = working.value ?? storedForm.value;
  return {
    ...out,
    ...(en ? { ctaEn: en } : {}),
    ...(ar ? { ctaAr: ar } : {}),
    ...(form ? { form } : {}),
  };
});

const isDirty = computed(
  () =>
    stored.value !== null &&
    draftDefinition.value !== null &&
    !sameGraph(draftDefinition.value, stored.value.definition)
);

const runPreview = async () => {
  if (!draftDefinition.value || accountId.value === null) return;
  asked += 1;
  const mine = asked;
  previewing.value = true;
  try {
    const result = await wasloApi.formPreview(
      accountId.value,
      draftDefinition.value,
      previewLang.value
    );
    if (mine !== asked) return;
    answer.value = result;
    previewFailed.value = false;
    if (seedNext) {
      seedNext = false;
      working.value = result.seed;
    }
  } catch {
    if (mine === asked) previewFailed.value = true;
  } finally {
    if (mine === asked) previewing.value = false;
  }
};

watch(
  [draftDefinition, previewLang],
  () => {
    clearTimeout(timer);
    // The first read of a task is asked at once; every edit after it waits for a pause.
    if (answer.value === null) runPreview();
    else timer = setTimeout(runPreview, PREVIEW_DELAY_MS);
  },
  { deep: true }
);

const edit = change => {
  const base = layoutForm.value;
  if (!base) return;
  working.value = change(base);
  serverProblems.value = [];
};
const setCta = pair => {
  cta.value = { ...cta.value, ...pair };
};
const splitForMe = () => {
  working.value = autoForm(
    layoutForm.value ?? working.value ?? storedForm.value
  );
  serverProblems.value = [];
};
const onToForm = stepId => edit(form => toForm(form, stepId));

const steps = computed(() =>
  Object.fromEntries(
    (stored.value?.definition?.steps ?? []).map(step => [step.id, step])
  )
);

/** Questions not on the form: the ones she can put back, and the ones that cannot be there. */
const QUESTION_TYPES = ['ask', 'choose', 'choose_live'];
const stays = computed(() =>
  (answer.value?.stayInChat ?? []).filter(stay =>
    QUESTION_TYPES.includes(steps.value[stay.stepId]?.type)
  )
);
const stayReason = reason => {
  const sentenceKey = `WASLO.FORMS.STAY.${String(reason ?? '').toUpperCase()}`;
  const sentence = t(sentenceKey);
  return sentence === sentenceKey ? t('WASLO.FORMS.STAY.UNTOLD') : sentence;
};

// ── errors and warnings ────────────────────────────────────────────────────────────────
const allErrors = computed(() =>
  mergeErrors(
    localErrors(layoutForm.value),
    answer.value?.errors ?? [],
    serverProblems.value.filter(problem =>
      String(problem?.problem ?? '').startsWith('form_')
    )
  )
);

const errorMessage = problem => {
  const detail = String(problem.detail ?? '');
  switch (problem.problem) {
    case 'form_page_empty':
      return t(key('ERR_EMPTY'));
    case 'form_too_many_pages':
      return t(key('ERR_PAGES'), { count: number(MAX_FORM_PAGES) });
    case 'form_too_many_questions':
      return t(key('ERR_QUESTIONS'), { count: number(MAX_FORM_QUESTIONS) });
    case 'form_branch_not_last':
      return t(key('ERR_BRANCH'));
    // Field fixes G1: a live question placed before the answer its choices are fetched with.
    case 'form_live_needs_earlier_page':
      return t(key('ERR_LIVE'));
    case 'form_step_twice':
      return t(key('ERR_TWICE'));
    // Services G2.4: photos and files go on the last page, once.
    case 'form_upload_not_last':
      return t(key('ERR_UPLOAD_LAST'));
    case 'form_upload_twice':
      return t(key('ERR_UPLOAD_TWICE'));
    case 'form_text_too_long':
      if (detail.includes(':next:')) {
        return t(key('ERR_LONG'), { count: number(MAX_FORM_BUTTON) });
      }
      if (detail.startsWith('intro:')) {
        return t(key('ERR_LONG_INTRO'), { count: number(MAX_FORM_INTRO) });
      }
      return t(key('ERR_LONG_TITLE'), { count: number(MAX_FORM_TITLE) });
    default:
      return t('WASLO.TASKS.EDITOR.FLOW.PROBLEMS.OTHER');
  }
};

/** Which page an error belongs to (0-based), or `null` for the form as a whole. */
const pageOf = problem => {
  const detail = String(problem.detail ?? '');
  if (problem.problem === 'form_page_empty') return Number(detail) - 1;
  const match = /^page:(\d+):/.exec(detail);
  return match ? Number(match[1]) - 1 : null;
};
const pageErrors = index =>
  allErrors.value
    .filter(problem => pageOf(problem) === index)
    .map(errorMessage);
const formErrors = computed(() =>
  allErrors.value
    .filter(problem => pageOf(problem) === null && !problem.stepId)
    .map(errorMessage)
);
const stepErrors = computed(() =>
  Object.fromEntries(
    allErrors.value
      .filter(problem => problem.stepId)
      .map(problem => [problem.stepId, errorMessage(problem)])
  )
);

const issueMessage = issue => {
  const step = steps.value[issue.stepId];
  const question = step ? questionWords(step, dashboardLang.value) : '';
  const detail = String(issue.detail ?? '');
  const language = t(
    detail.endsWith(':ar') ? 'WASLO.FORMS.LANG.AR' : 'WASLO.FORMS.LANG.EN'
  );
  switch (issue.warning) {
    case 'form_step_gone':
      return t(key('WARN_GONE_UNNAMED'));
    case 'form_step_not_eligible':
      return t(key('WARN_NOT_ELIGIBLE'), { question });
    case 'form_page_emptied':
      return t(key('WARN_PAGE_EMPTIED'), { n: number(Number(detail)) });
    case 'form_missing_language':
      return detail.startsWith('question:')
        ? t(key('WARN_QUESTION'), { question, language })
        : t(key('WARN_WORDS'), { language });
    default:
      return '';
  }
};
const issues = computed(() => [
  ...new Set(
    (answer.value?.layoutIssues ?? []).map(issueMessage).filter(Boolean)
  ),
]);

// ── saving ─────────────────────────────────────────────────────────────────────────────
const saveDisabled = computed(
  () => isSaving.value || !isDirty.value || allErrors.value.length > 0
);

const save = async () => {
  if (saveDisabled.value) return;
  isSaving.value = true;
  saveFailed.value = false;
  try {
    const saved = await wasloApi.updateTask(accountId.value, taskId.value, {
      definition: draftDefinition.value,
    });
    adopt(saved);
    savedAt.value = new Date();
    useAlert(t(key('SAVED')));
  } catch (error) {
    saveFailed.value = true;
    serverProblems.value = Array.isArray(error?.problems) ? error.problems : [];
    useAlert(t(key('SAVE_FAILED')));
  } finally {
    isSaving.value = false;
  }
};

const saveState = computed(() => {
  if (isSaving.value) return 'saving';
  if (saveFailed.value) return 'error';
  if (isDirty.value) return 'dirty';
  return savedAt.value ? 'saved' : 'idle';
});
const saveStatus = computed(() => {
  if (saveState.value === 'error') return t(key('SAVE_FAILED'));
  if (saveState.value === 'dirty') return t(key('STATUS_DIRTY'));
  if (saveState.value === 'saved') return t(key('STATUS_SAVED'));
  return '';
});

// ── leaving with work not saved ─────────────────────────────────────────────────────────
const warnIfUnsaved = event => {
  if (!isDirty.value) return;
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
};
onMounted(() => window.addEventListener('beforeunload', warnIfUnsaved));
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', warnIfUnsaved);
  clearTimeout(timer);
});
onBeforeRouteLeave(() => {
  if (!isDirty.value) return true;
  // eslint-disable-next-line no-alert
  return window.confirm(t('WASLO.TASKS.EDITOR.FLOW.LEAVE_WARNING'));
});

const backUrl = computed(() =>
  accountId.value === null
    ? ''
    : frontendURL(`accounts/${accountId.value}/waslo/tasks/${taskId.value}`)
);
const headerTitle = computed(() =>
  t(key('HEADER'), { task: stored.value?.title ?? '' })
);
</script>

<template>
  <WasloPageLayout
    :header-title="headerTitle"
    :back-url="backUrl"
    :back-label="stored?.title ?? ''"
    :is-fetching="task.isFetching.value"
    :error-message="task.errorMessage.value"
    :not-provisioned="task.isNotProvisioned.value"
    @retry="task.reload()"
  >
    <template #body>
      <div
        class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]"
        data-waslo-form-arrange
      >
        <div class="flex flex-col gap-5">
          <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
            {{ t(key('INTRO_NOTE')) }}
          </p>
          <p
            v-if="!storedForm"
            class="p-3 mb-0 text-sm rounded-xl bg-n-alpha-1 text-n-slate-12"
            data-waslo-form-arrange-new
          >
            {{ t(key('NEW_NOTE')) }}
          </p>
          <p
            v-for="error in formErrors"
            :key="error"
            class="mb-0 text-sm text-n-ruby-9"
            data-waslo-form-arrange-error
          >
            {{ error }}
          </p>
          <p
            v-for="issue in issues"
            :key="issue"
            class="mb-0 text-sm text-n-amber-11"
            data-waslo-form-arrange-issue
          >
            <bdi>{{ issue }}</bdi>
          </p>

          <BilingualBox
            :en="layoutForm?.titleEn ?? ''"
            :ar="layoutForm?.titleAr ?? ''"
            :label="t(key('FORM_TITLE'))"
            :max-length="MAX_FORM_TITLE"
            kind="task_question"
            :languages="languages"
            data-waslo-form-arrange-title
            @update:en="
              value => edit(form => setFormWords(form, { titleEn: value }))
            "
            @update:ar="
              value => edit(form => setFormWords(form, { titleAr: value }))
            "
            @update:pair="
              pair =>
                edit(form =>
                  setFormWords(form, { titleEn: pair.en, titleAr: pair.ar })
                )
            "
          />
          <BilingualBox
            :en="layoutForm?.introEn ?? ''"
            :ar="layoutForm?.introAr ?? ''"
            :label="t(key('INTRO'))"
            :max-length="MAX_FORM_INTRO"
            kind="task_question"
            :languages="languages"
            data-waslo-form-arrange-intro
            @update:en="
              value => edit(form => setFormWords(form, { introEn: value }))
            "
            @update:ar="
              value => edit(form => setFormWords(form, { introAr: value }))
            "
            @update:pair="
              pair =>
                edit(form =>
                  setFormWords(form, { introEn: pair.en, introAr: pair.ar })
                )
            "
          />

          <ol class="flex flex-col gap-4 mb-0 list-none">
            <FormPageCard
              v-for="(page, index) in pages"
              :key="`page-${index}`"
              :page="page"
              :index="index"
              :total="pages.length"
              :steps="steps"
              :lang="dashboardLang"
              :languages="languages"
              :cta="cta"
              :errors="pageErrors(index)"
              :step-errors="stepErrors"
              :can-remove="pages.length > 1"
              @up="stepId => edit(form => moveField(form, stepId, -1))"
              @down="stepId => edit(form => moveField(form, stepId, 1))"
              @move="(stepId, to) => edit(form => moveToPage(form, stepId, to))"
              @optional="
                (stepId, value) =>
                  edit(form => setOptional(form, stepId, value))
              "
              @chat="stepId => edit(form => toChat(form, stepId))"
              @words="patch => edit(form => setPageWords(form, index, patch))"
              @cta="setCta"
              @remove="edit(form => removePage(form, index))"
            />
          </ol>

          <div class="flex flex-wrap gap-3">
            <Button
              :label="t(key('ADD_PAGE'))"
              variant="outline"
              color="slate"
              size="sm"
              :disabled="pages.length >= MAX_FORM_PAGES"
              data-waslo-form-arrange-add
              @click="edit(addPage)"
            />
            <Button
              :label="t(key('AUTO'))"
              variant="faded"
              color="slate"
              size="sm"
              data-waslo-form-arrange-auto
              @click="splitForMe"
            />
          </div>

          <section
            v-if="stays.length"
            class="flex flex-col gap-2"
            data-waslo-form-arrange-chat
          >
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ t(key('IN_CHAT')) }}
            </p>
            <ul class="flex flex-col gap-2 mb-0 list-none">
              <li
                v-for="stay in stays"
                :key="stay.stepId"
                class="flex flex-wrap items-center gap-3 text-sm"
                :data-waslo-form-arrange-stay="stay.stepId"
              >
                <bdi class="text-n-slate-12">
                  {{
                    questionWords(steps[stay.stepId], dashboardLang) ||
                    stay.name
                  }}
                </bdi>
                <Button
                  v-if="stay.reason === 'in_chat'"
                  :label="t(key('TO_FORM'))"
                  variant="faded"
                  color="slate"
                  size="sm"
                  :disabled="questionCount(layoutForm) >= MAX_FORM_QUESTIONS"
                  data-waslo-form-arrange-to-form
                  @click="onToForm(stay.stepId)"
                />
                <span v-else class="text-n-slate-11">
                  {{ stayReason(stay.reason) }}
                </span>
              </li>
            </ul>
          </section>
        </div>

        <FormPhonePreview
          :answer="answer"
          :loading="previewing"
          :failed="previewFailed"
          :lang="previewLang"
          :show-language="languages === 'both'"
          @update:lang="value => (previewLang = value)"
        />
      </div>
    </template>

    <template #footer>
      <WasloSaveBar
        :state="saveState"
        :status="saveStatus"
        :save-label="t(key('SAVE'))"
        :disabled="saveDisabled"
        @save="save"
      />
    </template>
  </WasloPageLayout>
</template>
